import { useEffect, useState, type FormEvent } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';

const FORM_URL = 'https://readdy.ai/api/form/d9u4l8d3pcjqs2fcsm4g';

const milestones = [
  { year: '2024.01', title: '平台成立', desc: '樱花视频正式上线，立志成为最专业的樱花视频平台' },
  { year: '2024.03', title: '突破1000视频', desc: '平台收录超过1000个高清樱花视频，涵盖全球赏樱名所' },
  { year: '2024.06', title: '用户达50万', desc: '月活跃用户突破50万，成为国内领先的樱花主题视频社区' },
  { year: '2024.09', title: '全新改版', desc: '全面升级视觉设计和用户体验，新增分类导航和文章系统' },
];

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as const });
    document.title = '关于我们 - 樱花视频';
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = (formData.get('company_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      form.reset();
      return;
    }
    setFormStatus('loading');
    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          name: formData.get('name') as string,
          email: formData.get('email') as string,
          message: formData.get('message') as string,
        }).toString(),
      });
      const text = await res.text();
      let parsed: any = {};
      try { parsed = JSON.parse(text); } catch { /* ignore */ }
      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后再试';
        setFormStatus('error');
        setErrorMsg(msg);
      }
    } catch {
      setFormStatus('error');
      setErrorMsg('网络错误，请检查连接后重试');
    }
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于樱花视频',
    description: '樱花视频是专注樱花主题的高清视频平台，汇聚全球樱花盛开实录、延时摄影、旅行Vlog及赏樱攻略',
    about: {
      '@type': 'Organization',
      name: '樱花视频',
      url: 'https://sakuracam.github.io',
      description: '专注樱花主题的高清视频平台',
      foundingDate: '2024',
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'customer service',
        email: 'contact@yinghuavideo.com',
      },
    },
  };

  return (
    <div className="min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <Navbar />
      <main className="pt-20 pb-14">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />

          {/* Brand Story */}
          <section className="mt-10">
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 text-center">
              关于樱花视频
            </h1>
            <p className="mt-4 text-sm text-foreground-600 text-center max-w-2xl mx-auto leading-relaxed">
              我们相信，樱花不只是一种花，更是一种生活美学。樱花视频致力于为全球樱花爱好者提供最优质的高清樱花视频内容。
            </p>

            <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="text-xl font-heading font-bold text-foreground-950">
                  我们的故事
                </h2>
                <div className="mt-4 space-y-3">
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    樱花视频创立于2024年，由一群热爱樱花文化和视频创作的年轻人发起。我们发现，虽然网上有大量关于樱花的图片和文字内容，但高质量的樱花视频资源却较为分散。
                  </p>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    于是我们决定打造一个专注于樱花主题的高清视频平台，汇聚全球各地樱花盛开的实录、延时摄影作品、旅行Vlog和赏樱攻略。让每一位热爱樱花的人，无论身在何处，都能感受到樱花季的美好。
                  </p>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    从最初几个人的小团队开始，我们已经发展成为拥有超过1000个高清樱花视频、服务50万+用户的专业平台。未来，我们将继续深入探索樱花文化，为用户带来更多优质内容。
                  </p>
                </div>
              </div>
              <div className="rounded-xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=team%20of%20young%20creative%20people%20working%20together%20in%20a%20bright%20modern%20studio%20with%20cherry%20blossom%20wall%20art%20and%20plants%2C%20warm%20natural%20light%20streaming%20through%20large%20windows%2C%20collaborative%20creative%20atmosphere%2C%20editorial%20photography%20with%20soft%20warm%20tones%20and%20natural%20composition&width=800&height=500&seq=sakura-about-story&orientation=landscape"
                  alt="樱花视频团队"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </section>

          {/* Stats */}
          <section className="mt-16">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { value: '1000+', label: '高清视频' },
                { value: '50万+', label: '樱花爱好者' },
                { value: '5大', label: '内容分类' },
                { value: '99.8%', label: '用户好评率' },
              ].map((stat) => (
                <div key={stat.label} className="text-center p-6 rounded-xl bg-background-100 border border-background-200/70">
                  <div className="text-2xl md:text-3xl font-heading font-bold text-primary-600">
                    {stat.value}
                  </div>
                  <div className="mt-1 text-xs text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Milestones */}
          <section className="mt-16">
            <h2 className="text-xl font-heading font-bold text-foreground-950 text-center">
              发展历程
            </h2>
            <div className="mt-8 max-w-2xl mx-auto">
              {milestones.map((m, i) => (
                <div key={i} className="flex gap-4 pb-8 last:pb-0">
                  <div className="flex flex-col items-center">
                    <div className="w-3 h-3 rounded-full bg-primary-500 shrink-0 mt-1"></div>
                    {i < milestones.length - 1 && (
                      <div className="w-px flex-1 bg-background-200/70 mt-1"></div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="text-xs text-primary-600 font-medium">{m.year}</span>
                    <h3 className="mt-0.5 text-sm font-semibold text-foreground-950">{m.title}</h3>
                    <p className="mt-0.5 text-xs text-foreground-500 leading-relaxed">{m.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Contact Form */}
          <section id="contact" className="mt-16 max-w-2xl mx-auto">
            <h2 className="text-xl font-heading font-bold text-foreground-950 text-center">
              联系我们
            </h2>
            <p className="mt-2 text-sm text-foreground-600 text-center">
              有任何问题或建议？欢迎给我们留言
            </p>

            <form
              onSubmit={handleSubmit}
              data-readdy-form=""
              className="mt-8 space-y-5 p-6 rounded-xl bg-background-100 border border-background-200/70"
            >
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  姓名
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200/70 text-sm text-foreground-950 outline-none focus:border-primary-300 transition-colors"
                  placeholder="请输入你的姓名"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  邮箱
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200/70 text-sm text-foreground-950 outline-none focus:border-primary-300 transition-colors"
                  placeholder="请输入你的邮箱"
                />
              </div>
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  留言内容
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200/70 text-sm text-foreground-950 outline-none focus:border-primary-300 transition-colors resize-none"
                  placeholder="请输入你想说的话（最多500字）"
                ></textarea>
              </div>
              <input
                type="text"
                name="company_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="honeypot-field"
              />
              <button
                type="submit"
                disabled={formStatus === 'loading'}
                className="w-full py-3 rounded-lg bg-primary-500 text-white font-medium text-sm hover:bg-primary-600 transition-colors disabled:opacity-60 whitespace-nowrap"
              >
                {formStatus === 'loading' ? '提交中...' : '发送留言'}
              </button>
              {formStatus === 'success' && (
                <p className="text-sm text-green-600 text-center">留言已发送，我们会尽快回复你！</p>
              )}
              {formStatus === 'error' && (
                <p className="text-sm text-red-500 text-center">{errorMsg}</p>
              )}
            </form>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="flex items-center gap-3 p-4 rounded-lg bg-background-100 border border-background-200/70">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 shrink-0">
                  <i className="ri-mail-line text-lg"></i>
                </div>
                <div>
                  <p className="text-xs text-foreground-500">邮箱</p>
                  <p className="text-sm font-medium text-foreground-950">contact@yinghuavideo.com</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-4 rounded-lg bg-background-100 border border-background-200/70">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 shrink-0">
                  <i className="ri-wechat-line text-lg"></i>
                </div>
                <div>
                  <p className="text-xs text-foreground-500">微信</p>
                  <p className="text-sm font-medium text-foreground-950">樱花视频官方</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-4 rounded-lg bg-background-100 border border-background-200/70">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 shrink-0">
                  <i className="ri-bilibili-line text-lg"></i>
                </div>
                <div>
                  <p className="text-xs text-foreground-500">B站</p>
                  <p className="text-sm font-medium text-foreground-950">樱花视频Official</p>
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
      <ScrollToTop />
    </div>
  );
}